import React, { useContext, useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { StreamChat } from 'stream-chat';
import { AuthContext } from '../App';
import { Radio, Mic, MicOff, X, Volume2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import PTTWalkieRecorder from './PTTWalkieRecorder';
import useClaseEnCurso from '../hooks/useClaseEnCurso';

const WALKIE_MODE_KEY = 'ptt_walkie_mode';

export const isWalkieModeOn = () => {
  try { return localStorage.getItem(WALKIE_MODE_KEY) === 'on'; } catch (_e) { return false; }
};

export const setWalkieMode = (on) => {
  try { localStorage.setItem(WALKIE_MODE_KEY, on ? 'on' : 'off'); } catch (_e) { /* noop */ }
  try { window.dispatchEvent(new CustomEvent('iba:walkie-mode-changed', { detail: { on } })); } catch (_e) { /* noop */ }
};

/**
 * PTTWalkieListener — Escucha global de mensajes walkie entrantes.
 * Si el usuario tiene "Modo Radio" ON, reproduce el audio automáticamente
 * y muestra un overlay con la cara del emisor + botón "Responder PTT".
 */
const PTTWalkieListener = () => {
  const { API, user, getImageUrl } = useContext(AuthContext);
  const [client, setClient] = useState(null);
  const [modeOn, setModeOn] = useState(isWalkieModeOn());
  const [incoming, setIncoming] = useState(null); // {url, sender, senderId, senderPhoto, id}
  const [replyTarget, setReplyTarget] = useState(null);
  const audioRef = useRef(null);
  const seenIdsRef = useRef(new Set());
  const chirpCtxRef = useRef(null);

  // Sincronizar toggle con localStorage global
  useEffect(() => {
    const onChange = (e) => setModeOn(!!e.detail?.on);
    window.addEventListener('iba:walkie-mode-changed', onChange);
    return () => window.removeEventListener('iba:walkie-mode-changed', onChange);
  }, []);

  // 🔇 Silenciar Modo Radio automáticamente cuando hay clase en curso.
  // Evita que audios walkie interrumpan a estudiantes/profesores durante clase.
  const { hayClaseEnCurso } = useClaseEnCurso();
  const autoOffToastShownRef = useRef(false);
  useEffect(() => {
    if (hayClaseEnCurso && isWalkieModeOn()) {
      setWalkieMode(false);
      setModeOn(false);
      if (!autoOffToastShownRef.current) {
        autoOffToastShownRef.current = true;
        toast.info('🔇 Modo Radio silenciado — hay una clase en curso');
      }
    }
    if (!hayClaseEnCurso) {
      autoOffToastShownRef.current = false;
    }
  }, [hayClaseEnCurso]);

  // Chirp corto para "primar" el AudioContext y desbloquear auto-play
  const playChirp = useCallback(() => {
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      if (!chirpCtxRef.current) chirpCtxRef.current = new AC();
      const ctx = chirpCtxRef.current;
      if (ctx.state === 'suspended') ctx.resume().catch(() => {});
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.setValueAtTime(1200, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(800, ctx.currentTime + 0.12);
      gain.gain.setValueAtTime(0, ctx.currentTime);
      gain.gain.linearRampToValueAtTime(0.25, ctx.currentTime + 0.02);
      gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.15);
      osc.start();
      osc.stop(ctx.currentTime + 0.18);
    } catch (_e) { /* noop */ }
  }, []);

  // Conexión Stream Chat singleton
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const res = await axios.get(`${API}/stream-chat/token`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const { token: streamToken, user_id, user_name, user_image, api_key } = res.data || {};
        if (!streamToken || !user_id || !api_key || cancelled) return;
        const c = StreamChat.getInstance(api_key);
        if (c.userID !== user_id) {
          await c.connectUser(
            { id: user_id, name: user_name, image: user_image || undefined },
            streamToken
          );
        }
        if (!cancelled) setClient(c);
      } catch (e) {
        console.warn('PTTWalkieListener: no se pudo conectar Stream Chat', e?.message || e);
      }
    })();
    return () => { cancelled = true; };
  }, [API, user]);

  // Handler de mensajes walkie
  useEffect(() => {
    if (!client) return;
    const handler = (event) => {
      const msg = event?.message;
      if (!msg) return;
      if (msg.user?.id === client.userID) return; // no me disparo yo mismo
      const custom = msg.custom || {};
      const isWalkie = msg.walkie ?? custom.walkie;
      const walkieUrl = msg.walkie_url ?? custom.walkie_url;
      if (!isWalkie || !walkieUrl) return;
      const key = msg.id || `${walkieUrl}-${msg.created_at}`;
      if (seenIdsRef.current.has(key)) return;
      seenIdsRef.current.add(key);
      // Descartar walkies viejos (>90s) para no reproducir cosas del pasado al conectar
      const createdMs = msg.created_at ? new Date(msg.created_at).getTime() : 0;
      if (createdMs && Date.now() - createdMs > 90000) return;

      const senderName = msg.walkie_sender ?? custom.walkie_sender ?? msg.user?.name ?? 'Alguien';
      const senderPhoto = msg.walkie_sender_photo ?? custom.walkie_sender_photo ?? msg.user?.image ?? '';
      const senderId = msg.walkie_sender_id ?? custom.walkie_sender_id ?? msg.user?.id ?? '';
      const senderEmail = msg.user?.id?.replace(/_at_/, '@').replace(/_/g, '.') || '';

      if (isWalkieModeOn()) {
        setIncoming({
          id: key,
          url: walkieUrl,
          sender: senderName,
          senderId,
          senderEmail,
          senderPhoto,
        });
      }
    };
    client.on('message.new', handler);
    client.on('notification.message_new', handler);
    return () => {
      try { client.off('message.new', handler); } catch (_e) { /* noop */ }
      try { client.off('notification.message_new', handler); } catch (_e) { /* noop */ }
    };
  }, [client]);

  const [autoPlayFailed, setAutoPlayFailed] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioBlobUrl, setAudioBlobUrl] = useState('');
  const [audioLoading, setAudioLoading] = useState(false);
  const [audioError, setAudioError] = useState('');
  const currentBlobUrlRef = useRef('');

  // Al recibir walkie: fetch como blob (más robusto que <audio src=?token=>)
  // y reproducir automáticamente
  useEffect(() => {
    if (!incoming) return;
    setAutoPlayFailed(false);
    setIsPlaying(false);
    setAudioLoading(true);
    setAudioError('');
    setAudioBlobUrl('');

    let cancelled = false;

    // Beep clásico de bipper: 3 bips cortos secuenciales (~2200Hz, cada uno 100ms)
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (AC && !chirpCtxRef.current) chirpCtxRef.current = new AC();
      const ctx = chirpCtxRef.current;
      if (ctx?.state === 'suspended') ctx.resume().catch(() => {});
      if (ctx) {
        const now = ctx.currentTime;
        // 3 bips secuenciales
        for (let i = 0; i < 3; i++) {
          const t0 = now + i * 0.18; // gap 180ms entre bips
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.connect(gain); gain.connect(ctx.destination);
          osc.type = 'square';
          osc.frequency.setValueAtTime(2200, t0);
          gain.gain.setValueAtTime(0, t0);
          gain.gain.linearRampToValueAtTime(0.22, t0 + 0.01);
          gain.gain.setValueAtTime(0.22, t0 + 0.09);
          gain.gain.linearRampToValueAtTime(0, t0 + 0.11);
          osc.start(t0);
          osc.stop(t0 + 0.12);
        }
      }
    } catch (_e) { /* noop */ }
    try { navigator.vibrate?.([100, 60, 100, 60, 100]); } catch (_e) { /* noop */ }

    // Fetch audio como blob con Bearer auth (más robusto)
    const loadAudio = async () => {
      const rawUrl = String(incoming.url).startsWith('http') ? incoming.url : `${window.location.origin}${incoming.url}`;
      const token = localStorage.getItem('token') || '';
      // Reintentar hasta 3 veces con backoff corto (por si hay latencia backend/DB)
      let lastErr = null;
      for (let attempt = 0; attempt < 3; attempt++) {
        if (cancelled) return;
        try {
          const resp = await fetch(rawUrl, {
            headers: { Authorization: `Bearer ${token}` },
            cache: 'no-store',
          });
          if (!resp.ok) {
            lastErr = `HTTP ${resp.status}`;
            if (resp.status === 404) {
              // Race: DB aún no tiene el broadcast — esperar y reintentar
              await new Promise(r => setTimeout(r, 400 * (attempt + 1)));
              continue;
            }
            break;
          }
          const blob = await resp.blob();
          if (cancelled) return;
          if (blob.size < 200) {
            lastErr = 'audio vacío';
            await new Promise(r => setTimeout(r, 300));
            continue;
          }
          // Liberar blob URL previo
          if (currentBlobUrlRef.current) {
            try { URL.revokeObjectURL(currentBlobUrlRef.current); } catch (_e) { /* noop */ }
          }
          const blobUrl = URL.createObjectURL(blob);
          currentBlobUrlRef.current = blobUrl;
          setAudioBlobUrl(blobUrl);
          setAudioLoading(false);
          // Esperar al próximo tick para que el <audio> reciba el src, luego play()
          // 550ms permite que los 3 bips del bipper terminen antes de que empiece el audio
          setTimeout(() => {
            if (cancelled) return;
            const el = audioRef.current;
            if (!el) return;
            const p = el.play();
            if (p && typeof p.then === 'function') {
              p.then(() => setIsPlaying(true))
               .catch((err) => {
                 console.warn('Auto-play bloqueado:', err?.message);
                 setAutoPlayFailed(true);
               });
            }
          }, 550);
          return;
        } catch (e) {
          lastErr = e?.message || String(e);
          await new Promise(r => setTimeout(r, 300 * (attempt + 1)));
        }
      }
      if (!cancelled) {
        setAudioLoading(false);
        setAudioError(lastErr || 'Fallo desconocido');
        toast.error(`No se pudo cargar el audio (${lastErr})`);
      }
    };
    loadAudio();

    return () => {
      cancelled = true;
    };
  }, [incoming]);

  // Liberar blob URLs al desmontar
  useEffect(() => {
    return () => {
      if (currentBlobUrlRef.current) {
        try { URL.revokeObjectURL(currentBlobUrlRef.current); } catch (_e) { /* noop */ }
      }
    };
  }, []);

  const manualPlay = () => {
    const el = audioRef.current;
    if (!el) return;
    setAutoPlayFailed(false);
    el.play().then(() => setIsPlaying(true)).catch((err) => {
      toast.error('No se pudo reproducir. Verificá permisos de audio.');
      console.error(err);
    });
  };

  const retryLoad = () => {
    if (!incoming) return;
    // Forzar re-fetch simplemente re-emitiendo el incoming
    setAudioError('');
    setAudioBlobUrl('');
    const inc = incoming;
    setIncoming(null);
    setTimeout(() => setIncoming(inc), 50);
  };

  const finishPlayback = () => {
    setIncoming(null);
  };

  const openReply = () => {
    if (!incoming) return;
    // Necesitamos armar un target user object para el recorder
    const [nombre, ...rest] = (incoming.sender || '').split(' ');
    setReplyTarget({
      id: incoming.senderId,
      nombre: nombre || 'Usuario',
      apellido: rest.join(' '),
      foto_perfil: incoming.senderPhoto,
    });
    setIncoming(null);
  };

  const closeReply = () => setReplyTarget(null);

  const getInitials = (name) => {
    const parts = (name || '').split(' ');
    return `${(parts[0] || '')[0] || ''}${(parts[1] || '')[0] || ''}`.toUpperCase() || '?';
  };

  return (
    <>
      {/* Overlay walkie entrante */}
      {incoming && createPortal(
        <div
          className="fixed inset-0 z-[2147483645] flex items-center justify-center bg-black/60 backdrop-blur-md p-4"
          data-testid="walkie-incoming-overlay"
        >
          <div className="bg-gradient-to-br from-orange-600 via-amber-500 to-yellow-500 rounded-3xl shadow-2xl p-6 max-w-sm w-full text-white">
            <div className="text-center mb-4">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/20 backdrop-blur-sm text-xs font-bold mb-3 tracking-wide">
                <Radio className="w-3.5 h-3.5" />
                RADIO ENTRANTE
              </div>
              <div className="relative mx-auto w-28 h-28 mb-3">
                {incoming.senderPhoto ? (
                  <img
                    src={incoming.senderPhoto}
                    alt=""
                    className="w-28 h-28 rounded-full object-cover ring-4 ring-white/40 shadow-lg"
                  />
                ) : (
                  <div className="w-28 h-28 rounded-full bg-white/20 flex items-center justify-center text-3xl font-bold ring-4 ring-white/40 shadow-lg">
                    {getInitials(incoming.sender)}
                  </div>
                )}
                <div className="absolute -bottom-1 -right-1 w-10 h-10 rounded-full bg-red-600 border-4 border-white shadow flex items-center justify-center animate-pulse">
                  <Volume2 className="w-5 h-5 text-white" />
                </div>
              </div>
              <p className="text-xl font-bold truncate">{incoming.sender}</p>
              <p className="text-sm opacity-90 mt-1">
                {audioLoading ? 'Cargando audio...' :
                  audioError ? `⚠️ Error: ${audioError}` :
                  autoPlayFailed ? '⚠️ Tocá el botón para escuchar' :
                  (isPlaying ? 'Reproduciendo...' : 'Listo')}
              </p>
            </div>

            {/* Audio player — con blob URL (fetch autenticado) */}
            <audio
              ref={audioRef}
              src={audioBlobUrl || undefined}
              preload="auto"
              controls
              onPlay={() => { setIsPlaying(true); setAutoPlayFailed(false); }}
              onPause={() => setIsPlaying(false)}
              onEnded={finishPlayback}
              className="w-full mb-3"
              style={{ height: 36 }}
              data-testid="walkie-audio-player"
            />

            {autoPlayFailed && audioBlobUrl && (
              <button
                onClick={manualPlay}
                className="w-full mb-2 py-4 rounded-xl bg-white text-orange-700 font-bold text-lg flex items-center justify-center gap-2 shadow-lg animate-pulse hover:animate-none"
                data-testid="walkie-manual-play"
              >
                <Volume2 className="w-6 h-6" /> Tocar para escuchar
              </button>
            )}

            {audioError && (
              <button
                onClick={retryLoad}
                className="w-full mb-2 py-3 rounded-xl bg-white text-red-700 font-bold flex items-center justify-center gap-2 shadow-lg"
                data-testid="walkie-retry-load"
              >
                🔄 Reintentar cargar
              </button>
            )}

            <div className="grid grid-cols-2 gap-2 mt-2">
              <button
                onClick={finishPlayback}
                className="py-3 rounded-xl bg-white/15 hover:bg-white/25 backdrop-blur-sm text-white font-semibold flex items-center justify-center gap-2 transition-colors"
                data-testid="walkie-dismiss-btn"
              >
                <X className="w-4 h-4" /> Cerrar
              </button>
              <button
                onClick={openReply}
                disabled={!incoming.senderId}
                className="py-3 rounded-xl bg-white text-orange-700 font-bold flex items-center justify-center gap-2 hover:bg-orange-50 transition-colors disabled:opacity-50"
                data-testid="walkie-reply-btn"
              >
                <Mic className="w-4 h-4" /> Responder
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Modal de responder */}
      <PTTWalkieRecorder open={!!replyTarget} target={replyTarget} onClose={closeReply} />
    </>
  );
};

/**
 * Toggle Modo Radio — componente reutilizable para poner en el header o donde quieras.
 * Al primer ON, dispara un chirp para desbloquear el AudioContext del browser.
 */
export const WalkieModeToggle = ({ compact = false }) => {
  const [on, setOn] = useState(isWalkieModeOn());
  const { hayClaseEnCurso } = useClaseEnCurso();
  useEffect(() => {
    const onChange = (e) => setOn(!!e.detail?.on);
    window.addEventListener('iba:walkie-mode-changed', onChange);
    return () => window.removeEventListener('iba:walkie-mode-changed', onChange);
  }, []);

  const toggle = () => {
    if (hayClaseEnCurso) {
      toast.warning('🔇 No se puede activar Radio: hay una clase en curso.');
      return;
    }
    const next = !on;
    setWalkieMode(next);
    setOn(next);
    if (next) {
      // Chirp para "primar" audio context — desbloquea auto-play luego
      try {
        const AC = window.AudioContext || window.webkitAudioContext;
        if (AC) {
          const ctx = new AC();
          if (ctx.state === 'suspended') ctx.resume().catch(() => {});
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.connect(gain); gain.connect(ctx.destination);
          osc.type = 'sine';
          osc.frequency.value = 1000;
          gain.gain.setValueAtTime(0.2, ctx.currentTime);
          gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.12);
          osc.start();
          osc.stop(ctx.currentTime + 0.15);
        }
      } catch (_e) { /* noop */ }
      toast.success('🎙️ Modo Radio activado — las notas walkie se reproducirán solas');
    } else {
      toast.info('🔇 Modo Radio apagado');
    }
  };

  if (compact) {
    return (
      <button
        onClick={toggle}
        disabled={hayClaseEnCurso}
        className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold transition-colors ${
          hayClaseEnCurso ? 'bg-gray-100 text-gray-400 cursor-not-allowed opacity-70' :
          on ? 'bg-orange-500 text-white shadow' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
        }`}
        title={hayClaseEnCurso ? '🔇 Bloqueado durante clase en curso' : (on ? 'Modo Radio ON — auto-reproducción' : 'Modo Radio OFF')}
        data-testid="walkie-mode-toggle"
      >
        {hayClaseEnCurso ? <MicOff className="w-3.5 h-3.5" /> : (on ? <Radio className="w-3.5 h-3.5" /> : <MicOff className="w-3.5 h-3.5" />)}
        {hayClaseEnCurso ? 'En clase' : (on ? 'Radio ON' : 'Radio OFF')}
      </button>
    );
  }

  return (
    <button
      onClick={toggle}
      disabled={hayClaseEnCurso}
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
        hayClaseEnCurso ? 'bg-gray-100 text-gray-400 cursor-not-allowed opacity-70' :
        on ? 'bg-orange-500 text-white shadow' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
      }`}
      title={hayClaseEnCurso ? '🔇 Bloqueado durante clase en curso' : undefined}
      data-testid="walkie-mode-toggle"
    >
      {hayClaseEnCurso ? <MicOff className="w-4 h-4" /> : (on ? <Radio className="w-4 h-4" /> : <MicOff className="w-4 h-4" />)}
      {hayClaseEnCurso ? 'Radio silenciado (clase)' : `Modo Radio ${on ? 'ON' : 'OFF'}`}
    </button>
  );
};

export default PTTWalkieListener;
